import _ from "lodash";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  FieldDefinitionFieldType,
  FieldDefinitionSelectType,
  FieldImperativeHandle,
  FieldSizeType,
} from "@/components/@types";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { Values } from "@/constants";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

type SelectOption = { key: string; value: string; label: string };

function FieldSelectValueInteractive({
  value,
  valueOptions,
  style,
  events,
  size = "md",
  ref,
}: FieldDefinitionFieldType<FieldDefinitionSelectType<any>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isPopperOpen, setIsPopperOpenState] = useState(false);
  const selectOptions = (valueOptions?.options || []) as SelectOption[];
  const placeholder = valueOptions?.placeholder || "Select option...";
  const testId = (valueOptions as any)?.testId as string | undefined;

  const [transientValue, setTransientValue] = useState<string | undefined>(
    typeof value === "string" ? value : undefined,
  );

  const setIsPopperOpen = useCallback(
    (isOpen: boolean) => {
      setIsPopperOpenState(isOpen ?? false);
      if (!isOpen) {
        events?.onChangeField?.(transientValue);
        events?.onSubmitField?.();
      }
    },
    [events, transientValue],
  );

  useEffect(() => {
    setTransientValue(typeof value === "string" ? value : undefined);
  }, [value]);

  const handleItemClick = useCallback(
    (option: SelectOption) => {
      setTransientValue(option.value);
      setIsPopperOpenState(false);
      events?.onChangeField?.(option.value);
      events?.onSubmitField?.();
    },
    [events],
  );

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        triggerRef.current?.focus();
        setIsPopperOpen(true);
      },
      unsetFocus: () => {
        triggerRef.current?.blur();
        setIsPopperOpen(false);
      },
    }),
    [],
  );

  const selectedLabel = selectOptions.find(
    (o) => o.value === transientValue,
  )?.label;

  const triggerContent = (
    <div
      ref={triggerRef}
      tabIndex={0}
      role="button"
      data-testid={testId ?? `field-select-${transientValue || "empty"}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsPopperOpen(true);
        }
      }}
      style={{
        paddingTop: "1px",
        paddingBottom: "1px",
        paddingRight: "2px",
        marginTop: "6px",
        ...((typographyStyles as Record<string, React.CSSProperties>)[
          `body-${size}`
        ] as React.CSSProperties),
        color: selectedLabel
          ? "var(--foreground)"
          : "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
        borderRadius: "calc(var(--radius) - 4px)",
        maxWidth: "100%",
        width: "fit-content",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flexWrap: "nowrap",
        textWrap: "nowrap",
        cursor: "pointer",
        outline: "none",
        fontStyle: selectedLabel ? "normal" : "italic",
      }}
    >
      {selectedLabel || placeholder}
    </div>
  );

  return (
    <div style={{ width: "fit-content", maxWidth: "100%", ...style }}>
      <Popper
        anchor={triggerContent}
        placement="bottom-start"
        isClickable={true}
        isHoverable={false}
        isOpenState={[isPopperOpen, setIsPopperOpen]}
        floatingStyle={{
          maxHeight: "400px",
          overflowY: "auto",
        }}
      >
        <List<SelectOption>
          data={selectOptions}
          getRowKey={(option) => option.value}
          getIsChecked={(option) => option.value === transientValue}
          isMultiselect={false}
          isNullable={true}
          onItemClick={handleItemClick}
          size="sm"
        >
          <List.Items<SelectOption>
            children={(option: SelectOption) => (
              <List.Item
                value={option}
                data-testid={
                  testId
                    ? `${testId}-option-${option.value}`
                    : `field-select-option-${option.value}`
                }
              >
                <div
                  style={{
                    ...((
                      typographyStyles as Record<string, React.CSSProperties>
                    )[`body-${size}`] as React.CSSProperties),
                    color: "var(--foreground)",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                    width: "100%",
                  }}
                >
                  {option.label}
                </div>
              </List.Item>
            )}
          />
        </List>
      </Popper>
    </div>
  );
}

function FieldSelectValue({
  value,
  valueOptions,
  style,
  events,
  size = "md",
  ref,
}: FieldDefinitionFieldType<FieldDefinitionSelectType<any>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const selectOptions = (valueOptions?.options || []) as SelectOption[];
  const selectedLabel = selectOptions.find((o) => o.value === value)?.label;

  if (events) {
    return (
      <FieldSelectValueInteractive
        value={value}
        valueOptions={valueOptions}
        style={style}
        events={events}
        size={size}
        ref={ref}
      />
    );
  }

  return (
    <div style={{ width: "fit-content", maxWidth: "100%", ...style }}>
      <div
        style={{
          ...((typographyStyles as Record<string, React.CSSProperties>)[
            `body-${size}`
          ] as React.CSSProperties),
          color: selectedLabel
            ? "var(--foreground)"
            : "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
          fontStyle: selectedLabel ? "normal" : "italic",
        }}
      >
        {selectedLabel || "—"}
      </div>
    </div>
  );
}

function FieldSelectSkeleton({ size = "md" }: { size?: FieldSizeType }) {
  return (
    <Skeleton
      variant="text"
      level={`body-${size}`}
      style={{
        width: "75%",
        maxWidth: Values.DEFAULT_COLUMN_WIDTH,
      }}
    />
  );
}

export function FieldSelect({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionSelectType<any>> & {
  isLoading?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const internalRef = useRef<FieldImperativeHandle>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        internalRef.current?.setFocus();
      },
      unsetFocus: () => {
        internalRef.current?.unsetFocus();
      },
    }),
    [],
  );

  if (isLoading) {
    return <FieldSelectSkeleton size={props.size} />;
  }

  return <FieldSelectValue {...props} ref={internalRef} />;
}
